// ================================================================
// FieldRenderer — رندر فیلدها از روی schema با پشتیبانی شرطی
// ================================================================

import { t } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';
import { shouldShowField, cleanInvalidValues } from '../../nodes/start/config-schema.js';
import { resolveOptions } from '../../nodes/ai-analysis/dynamic-options.js';

/**
 * رندر تمام فیلدهای یه schema بر اساس مقادیر فعلی
 */
export function renderFields(schema, config, nodeDef) {
  return schema.fields
    .filter(field => shouldShowField(field, config))
    .map(field => renderField(field, config, nodeDef))
    .join('');
}

function renderField(field, config, nodeDef) {
  const value = config[field.key] !== undefined
    ? config[field.key]
    : (field.defaultValue !== undefined ? field.defaultValue : '');

  const label = t(field.labelKey);
  const required = field.required
    ? '<span class="field-required">*</span>'
    : '';

  let inputHtml = '';

  switch (field.type) {
    case 'select':
      inputHtml = renderDropdown(field, value, config);
      break;
    case 'provider':
      inputHtml = renderProvider(field);
      break;
    case 'oauth':
      inputHtml = renderOAuth(field, value);
      break;
    case 'text':
    case 'email':
    case 'password':
    case 'number': {
      const inputType = field.type;
      const fieldIcon = field.icon || (
        field.type === 'email' ? 'alternate_email' :
        field.type === 'password' ? 'lock' : null
      );
      inputHtml = renderTextInput(field, value, inputType, fieldIcon);
      break;
    }
    case 'textarea':
      inputHtml = renderTextarea(field, value);
      break;
    case 'checkbox':
      inputHtml = renderCheckbox(field, value);
      break;
    case 'repeater':
      inputHtml = renderRepeater(field, value);
      break;
    default:
      inputHtml = `<div>Unknown field type: ${field.type}</div>`;
  }

  // checkbox label خودش رو داره
  if (field.type === 'checkbox') {
    return `
      <div class="field" data-field-key="${field.key}">
        ${inputHtml}
        ${field.help ? `<p class="field-help">${escapeHtml(t(field.help))}</p>` : ''}
      </div>
    `;
  }

  return `
    <div class="field" data-field-key="${field.key}">
      <label class="field-label">
        ${escapeHtml(label)}
        ${required}
      </label>
      ${inputHtml}
      ${field.help ? `<p class="field-help">${escapeHtml(t(field.help))}</p>` : ''}
    </div>
  `;
}

// ─────────────────────────────────────────────────────────
// Dropdown سفارشی
// ─────────────────────────────────────────────────────────
function optLabel(opt) {
  return opt.label !== undefined ? opt.label : t(opt.labelKey);
}

function renderDropdown(field, value, config) {
  const options = resolveOptions(field, config);
  const placeholder = field.placeholderKey ? t(field.placeholderKey) : '';
  const selectedOption = options.find(o => o.value === value);
  const displayLabel = selectedOption ? optLabel(selectedOption) : placeholder;
  const hasValue = !!selectedOption;

  return `
    <div class="dropdown" data-dropdown="${field.key}">
      <button type="button" class="dropdown-trigger ${hasValue ? 'has-value' : ''}" data-field="${field.key}">
        <span>${escapeHtml(displayLabel)}</span>
        <span class="dropdown-arrow material-symbols-rounded" style="font-size: 18px">expand_more</span>
      </button>
      <div class="dropdown-menu">
        ${options.map(opt => opt.disabled
          ? `
          <div class="dropdown-option dropdown-option-disabled">
            ${escapeHtml(optLabel(opt))}
          </div>
          `
          : `
          <div class="dropdown-option ${opt.value === value ? 'selected' : ''}"
               data-value="${escapeHtml(opt.value)}"
               data-field="${field.key}">
            ${escapeHtml(optLabel(opt))}
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────
// Provider (نمایش فقط — مثل Twilio)
// ─────────────────────────────────────────────────────────
function renderProvider(field) {
  const p = field.provider;
  return `
    <div class="field-provider">
      <div class="field-provider-icon" style="background: ${p.color}">
        ${icon(p.icon, 22)}
      </div>
      <span class="field-provider-name">${escapeHtml(t(p.nameKey))}</span>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────
// OAuth (با دکمه Connect یا نمایش اتصال موجود)
// ─────────────────────────────────────────────────────────
function renderOAuth(field, value) {
  const p = field.provider;

  // اگه قبلاً متصل شده، آدرس ایمیل نشون بده
  if (value && typeof value === "object" && value.email) {
    const justConnected = value._justConnected ? " oauth-just-connected" : "";
    return `
      <div class="oauth-connected${justConnected}">
        <div class="oauth-icon" style="background: ${p.color}">
          ${icon(p.icon, 20)}
        </div>
        <div class="oauth-info">
          <div class="oauth-name">${escapeHtml(t(p.nameKey))}</div>
          <div class="oauth-email">${escapeHtml(value.email)}</div>
        </div>
        <span class="oauth-status-badge" title="Verbunden">
          ${icon("check_circle", 18)}
        </span>
        ${/* Hebt nur die Verknüpfung DIESES Knotens auf — das Konto bleibt mit der
              Organisation verbunden (E-Mail-Seite und Dokumentversand nutzen es weiter).
              Wirklich trennen kann man es in Einstellungen → Verbindungen. */ ''}
        <button type="button" class="oauth-disconnect" data-field="${field.key}" data-action="disconnect"
                title="Verknüpfung dieses Knotens aufheben — das Konto bleibt in Einstellungen → Verbindungen bestehen">
          ${icon("logout", 16)}
        </button>
      </div>
      <button type="button" class="oauth-test" data-connection-id="${escapeHtml(value.connectionId || '')}">
        ${icon('science', 16)}
        <span>${escapeHtml(t('nodes.email.testEmails'))}</span>
      </button>
      <div class="oauth-test-result" data-test-result></div>
    `;  }

  // در غیر این صورت دکمه Connect
  return `
    <button type="button" class="oauth-connect" data-field="${field.key}" data-provider="${p.id}" data-auth-url="${p.authUrl || ''}">
      <div class="oauth-icon" style="background: ${p.color}">
        ${icon(p.icon, 20)}
      </div>
      <span class="oauth-connect-label">${escapeHtml(t('nodes.email.connectTo', { provider: t(p.nameKey) }))}</span>
      ${icon('open_in_new', 16)}
    </button>
  `;
}

// ─────────────────────────────────────────────────────────
// Text / Email / Password / Number input
// ─────────────────────────────────────────────────────────
function renderTextInput(field, value, inputType, fieldIcon) {
  const iconHtml = fieldIcon
    ? `<span class="text-input-icon">${icon(fieldIcon, 18)}</span>`
    : '';

  const inputAttrs = inputType === 'number'
    ? `min="1" max="65535"`
    : `autocomplete="off" spellcheck="false"`;

  return `
    <div class="text-input-wrap ${fieldIcon ? 'has-icon' : ''}">
      ${iconHtml}
      <input
        type="${inputType}"
        class="text-input"
        data-field="${field.key}"
        data-type="${inputType}"
        value="${escapeHtml(String(value))}"
        placeholder="${escapeHtml(t(field.placeholderKey || ''))}"
        ${inputAttrs}
      />
    </div>
  `;
}

// ─────────────────────────────────────────────────────────
// Textarea (چندخطی)
// ─────────────────────────────────────────────────────────
function renderRepeater(field, value) {
  const items = Array.isArray(value) ? value : [];
  const placeholder = escapeHtml(t(field.placeholderKey || ''));
  const addLabel = escapeHtml(t(field.addLabelKey || ''));
  const rows = items.map((item, i) => `
    <div class="repeater-row">
      <input type="text" class="repeater-input" data-field="${field.key}" data-index="${i}" value="${escapeHtml(String(item))}" placeholder="${placeholder}" />
      <button type="button" class="repeater-remove" data-repeater-remove="${field.key}" data-index="${i}">${icon('close', 18)}</button>
    </div>
  `).join('');
  return `
    <div class="repeater" data-repeater="${field.key}">
      ${rows}
      <button type="button" class="repeater-add" data-repeater-add="${field.key}">${icon('add', 18)} <span>${addLabel}</span></button>
    </div>
  `;
}

function renderTextarea(field, value) {
  const rows = field.rows || 5;
  return `
    <textarea
      class="textarea-input"
      data-field="${field.key}"
      rows="${rows}"
      placeholder="${escapeHtml(t(field.placeholderKey || ''))}"
      spellcheck="false"
    >${escapeHtml(String(value))}</textarea>
  `;
}

// ─────────────────────────────────────────────────────────
// Checkbox
// ─────────────────────────────────────────────────────────
function renderCheckbox(field, value) {
  const checked = value === true || value === 'true';
  return `
    <label class="checkbox-row">
      <input type="checkbox" class="checkbox-input" data-field="${field.key}" ${checked ? 'checked' : ''} />
      <span class="checkbox-box">${icon('check', 14)}</span>
      <span class="checkbox-label">${escapeHtml(t(field.labelKey))}</span>
    </label>
  `;
}

// ─────────────────────────────────────────────────────────
// خواندن مقادیر فعلی از DOM
// ─────────────────────────────────────────────────────────
export function readValues(modalEl) {
  const values = {};

  // dropdownها
  modalEl.querySelectorAll('.dropdown-trigger').forEach(trigger => {
    const key = trigger.dataset.field;
    const selected = trigger.closest('.dropdown')?.querySelector('.dropdown-option.selected');
    if (selected) {
      values[key] = selected.dataset.value;
    }
  });

  // textها (text, email, password, number)
  modalEl.querySelectorAll('input.text-input').forEach(input => {
    const key = input.dataset.field;
    const type = input.dataset.type;
    values[key] = type === 'number' ? Number(input.value) : input.value;
  });

  // textareaها
  modalEl.querySelectorAll('textarea.textarea-input').forEach(ta => {
    values[ta.dataset.field] = ta.value;
  });

  // checkboxها
  modalEl.querySelectorAll('input.checkbox-input').forEach(input => {
    values[input.dataset.field] = input.checked;
  });

  // repeaterها (آرایه، خطوط خالی حذف)
  modalEl.querySelectorAll('.repeater').forEach(rep => {
    const key = rep.dataset.repeater;
    const inputs = [...rep.querySelectorAll('input.repeater-input')];
    values[key] = inputs.map(inp => inp.value).filter(v => v.trim() !== '');
  });

  return values;
}

export { cleanInvalidValues };
